import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import type {
    SketchParametricImplicitCurveDescriptor,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';
import {SKETCH_PARAMETRIC_TOLERANCE} from '../core/SketchParametricToleranceConfig';
import {
    readSketchConstraintBranchHintNumber,
    readSketchConstraintBranchHintPoint,
} from '../utils/readSketchConstraintBranchHint';

/**
 * 相切关系直线退化判定阈值（米）。
 */
const TANGENT_LINE_DEGENERATE_LENGTH = SKETCH_PARAMETRIC_TOLERANCE.degenerateLength;

/**
 * 直线长度与圆系关系公式编译器。
 *
 * 职责：编译线/圆系相等、同心、共径、真实曲线长度相等及当前已参数化的相切组合。
 * 直线—椭圆使用支撑函数公式；样条和曲线两两相切仍需显式接触参数与导数。
 */
export class SketchCurveConstraintFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 当前已迁移的关系类型。 */
    public readonly relationKinds = [
        'Equal',
        'Concentric',
        'Coradial',
        'EqualCurveLength',
        'Tangent',
    ] as const;

    /**
     * 编译直线长度或圆系关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        switch (context.constraintState.relationKind) {
            case 'Equal':
                return this.compileEqual(context);
            case 'Concentric':
                return this.compileConcentric(context);
            case 'Coradial':
                return this.compileCoradial(context);
            case 'EqualCurveLength':
                return this.compileEqualCurveLength(context);
            case 'Tangent':
                return this.compileTangent(context);
            default:
                return 'not-applicable';
        }
    }

    /**
     * 编译相等关系。
     *
     * SOLIDWORKS 中直线比较长度，圆和圆弧比较半径；不能按统一包围盒或控制点距离处理。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileEqual(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const entityIds = context.resolveDirectEntityIds();

        if (entityIds.length < 2) {
            context.reportUnsupported('相等关系需要两个以上实体目标');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const lines = entityIds
            .map((entityId) => context.linePointHandles.get(entityId))
            .filter((line) => Boolean(line));

        if (lines.length === entityIds.length) {
            const anchor = lines[0]!;

            lines.slice(1).forEach((line, index) => {
                context.system.addEquation(SketchParametricEquationFactory.equalLength(
                    `${equationId}:line:${index}`,
                    anchor![0],
                    anchor![1],
                    line![0],
                    line![1],
                ));
            });
            return 'compiled';
        }

        const radiusParameterIds = entityIds
            .map((entityId) => context.radiusParameterIds.get(entityId))
            .filter((parameterId): parameterId is string => Boolean(parameterId));

        if (radiusParameterIds.length === entityIds.length) {
            const anchor = radiusParameterIds[0]!;

            radiusParameterIds.slice(1).forEach((parameterId, index) => {
                context.system.addEquation(SketchParametricEquationFactory.equalRadius(
                    `${equationId}:radius:${index}`,
                    anchor,
                    parameterId,
                ));
            });
            return 'compiled';
        }

        context.reportUnsupported('相等关系当前仅支持「线-线等长」与「圆/弧-圆/弧等半径」');
        return 'rejected';
    }

    /**
     * 编译同心关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileConcentric(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const entityIds = context.resolveDirectEntityIds();
        const directPoints = context.resolveDirectPointHandles();
        const centers = entityIds
            .map((entityId) => context.centerPointHandles.get(entityId))
            .filter((center) => Boolean(center));

        if (centers.length !== entityIds.length
            || directPoints.length + centers.length < 2
            || directPoints.length > 1) {
            context.reportUnsupported('同心关系需要两个以上圆心目标，或一个点与一个以上圆系目标');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const anchor = directPoints[0] ?? centers[0]!;
        const targets = directPoints.length > 0 ? centers : centers.slice(1);

        targets.forEach((center, index) => {
            context.system.addEquation(SketchParametricEquationFactory.coincident(
                `${equationId}:center:${index}`,
                anchor,
                center!,
            ));
        });
        return 'compiled';
    }

    /**
     * 编译共径关系。
     *
     * 持久化解析边界已把历史 `Congruent` 显式迁移为唯一领域标识 `Coradial`，
     * 公式编译层不承担协议兼容分支。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileCoradial(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const entityIds = context.resolveDirectEntityIds();
        const centers = entityIds
            .map((entityId) => context.centerPointHandles.get(entityId))
            .filter((center) => Boolean(center));
        const radiusParameterIds = entityIds
            .map((entityId) => context.radiusParameterIds.get(entityId))
            .filter((parameterId): parameterId is string => Boolean(parameterId));

        if (entityIds.length < 2
            || centers.length !== entityIds.length
            || radiusParameterIds.length !== entityIds.length) {
            context.reportUnsupported('共径关系需要两个以上具备圆心和半径参数的圆/弧目标');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const anchorCenter = centers[0]!;
        const anchorRadiusParameterId = radiusParameterIds[0]!;

        centers.slice(1).forEach((center, index) => {
            context.system.addEquation(SketchParametricEquationFactory.coincident(
                `${equationId}:center:${index}`,
                anchorCenter,
                center!,
            ));
            context.system.addEquation(SketchParametricEquationFactory.equalRadius(
                `${equationId}:radius:${index}`,
                anchorRadiusParameterId,
                radiusParameterIds[index + 1]!,
            ));
        });
        return 'compiled';
    }

    /**
     * 编译任意已参数化曲线的真实长度相等关系。
     *
     * 线使用端点距离，圆使用 `2πr`，圆弧使用 `r×扫角`，样条使用统一尺度参数
     * 承载的采样弧长。目标类型选择只发生在系统构建阶段，本编译器不读取运行时对象。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileEqualCurveLength(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const entityIds = context.resolveDirectEntityIds();
        const first = context.curveLengthSegments.get(entityIds[0] ?? '');
        const second = context.curveLengthSegments.get(entityIds[1] ?? '');

        if (entityIds.length !== 2 || !first || !second) {
            context.reportUnsupported('曲线长度相等关系需要两个具备统一长度描述符的曲线目标');
            return 'rejected';
        }

        context.system.addEquation(SketchParametricEquationFactory.equalCurveLength(
            `constraint:${context.constraintState.constraintId}`,
            first,
            second,
        ));
        return 'compiled';
    }

    /**
     * 编译当前已参数化的相切关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileTangent(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const entityIds = context.resolveDirectEntityIds();
        const lineEntityId = entityIds.find((entityId) => context.linePointHandles.has(entityId));
        const curveEntityIds = entityIds.filter((entityId) => (
            context.centerPointHandles.has(entityId) && context.radiusParameterIds.has(entityId)
        ));
        const ellipseEntityIds = entityIds.filter((entityId) => context.ellipseGeometryStates.has(entityId));

        if (lineEntityId && curveEntityIds.length === 1) {
            return this.compileLineCircleTangent(context, lineEntityId, curveEntityIds[0]!);
        }

        if (lineEntityId && curveEntityIds.length === 0 && ellipseEntityIds.length === 1) {
            return this.compileLineEllipseTangent(context, lineEntityId, ellipseEntityIds[0]!);
        }

        if (!lineEntityId && curveEntityIds.length === 2) {
            return this.compileCirclePairTangent(context, curveEntityIds[0]!, curveEntityIds[1]!);
        }

        const implicitCurveEntityIds = entityIds
            .filter((entityId) => context.implicitCurveDescriptors.has(entityId));

        if (!lineEntityId && implicitCurveEntityIds.length === 2) {
            return this.compileImplicitCurvePairTangent(
                context,
                implicitCurveEntityIds[0]!,
                implicitCurveEntityIds[1]!,
            );
        }

        const splineEntityIds = entityIds
            .filter((entityId) => context.splineCurveDescriptors.has(entityId));

        if (splineEntityIds.length === 1 && implicitCurveEntityIds.length === 1) {
            // 精确样条相切交给后续样条公式编译器，保持关系族按曲线能力扩展。
            return 'not-applicable';
        }

        context.reportUnsupported('相切关系当前支持「直线 + 圆/圆弧/椭圆」或「圆系 + 圆系」；其他曲线组合待接触参数化');
        return 'rejected';
    }

    /**
     * 编译圆系—椭圆或椭圆—椭圆相切关系。
     *
     * @param context 编译上下文。
     * @param firstEntityId 第一曲线实体 ID。
     * @param secondEntityId 第二曲线实体 ID。
     * @returns 编译结果。
     */
    private compileImplicitCurvePairTangent(
        context: SketchConstraintFormulaCompileContext,
        firstEntityId: string,
        secondEntityId: string,
    ): SketchConstraintFormulaCompileResult {
        const first = context.implicitCurveDescriptors.get(firstEntityId)!;
        const second = context.implicitCurveDescriptors.get(secondEntityId)!;
        const persistedContact = readSketchConstraintBranchHintPoint(
            context.constraintState,
            'implicit-curve-contact',
            'point',
        );
        const firstCenterX = context.system.read(first.center.xId);
        const firstCenterY = context.system.read(first.center.yId);
        const secondCenterX = context.system.read(second.center.xId);
        const secondCenterY = context.system.read(second.center.yId);
        const centerDeltaX = secondCenterX - firstCenterX;
        const centerDeltaY = secondCenterY - firstCenterY;
        const centerDistance = Math.hypot(centerDeltaX, centerDeltaY);

        if (!persistedContact && centerDistance < TANGENT_LINE_DEGENERATE_LENGTH) {
            context.reportUnsupported('两条隐式曲线中心重合，无法确定稳定相切接触分支');
            return 'rejected';
        }

        const directionX = persistedContact ? 0 : centerDeltaX / centerDistance;
        const directionY = persistedContact ? 0 : centerDeltaY / centerDistance;
        const firstBoundary = persistedContact
            ?? this.resolveImplicitCurveBoundaryPoint(context, first, directionX, directionY);
        const secondBoundary = persistedContact
            ?? this.resolveImplicitCurveBoundaryPoint(context, second, -directionX, -directionY);

        if (!firstBoundary || !secondBoundary) {
            context.reportUnsupported('隐式曲线存在退化半径或轴长，无法建立相切接触点');
            return 'rejected';
        }

        /**
         * 接触点是方程内部瞬态未知量，不属于草图运行时点，也不会写回实体。
         * 初值取两曲线相向边界点中点，使已近似相切的旧对象几何能稳定延续原接触支。
         */
        const contact = context.system.addPoint(
            `constraint:${context.constraintState.constraintId}:contact`,
            persistedContact?.[0] ?? (firstBoundary[0] + secondBoundary[0]) / 2,
            persistedContact?.[1] ?? (firstBoundary[1] + secondBoundary[1]) / 2,
        );

        /** 求解成功后记录真实接触点，下一次构建优先延续同一多解分支。 */
        context.registerResolvedBranchHint(() => ({
            kind: 'implicit-curve-contact',
            point: [context.system.read(contact.xId), context.system.read(contact.yId)],
        }));

        context.system.addEquation(SketchParametricEquationFactory.implicitCurvePairTangent(
            `constraint:${context.constraintState.constraintId}`,
            contact,
            first,
            second,
        ));
        return 'compiled';
    }

    /**
     * 计算隐式曲线沿指定中心射线的边界点，供相切接触参数生成稳定 seed。
     *
     * @param context 编译上下文。
     * @param curve 隐式曲线。
     * @param directionX 单位射线 x 分量。
     * @param directionY 单位射线 y 分量。
     * @returns 边界点局部坐标；曲线退化时返回 null。
     */
    private resolveImplicitCurveBoundaryPoint(
        context: SketchConstraintFormulaCompileContext,
        curve: SketchParametricImplicitCurveDescriptor,
        directionX: number,
        directionY: number,
    ): readonly [number, number] | null {
        const centerX = context.system.read(curve.center.xId);
        const centerY = context.system.read(curve.center.yId);

        if (curve.kind === 'circle') {
            const radius = context.system.read(curve.radiusParameterId);

            return radius > TANGENT_LINE_DEGENERATE_LENGTH
                ? [centerX + directionX * radius, centerY + directionY * radius]
                : null;
        }

        const majorX = context.system.read(curve.majorPositive.xId) - centerX;
        const majorY = context.system.read(curve.majorPositive.yId) - centerY;
        const minorX = context.system.read(curve.minorPositive.xId) - centerX;
        const minorY = context.system.read(curve.minorPositive.yId) - centerY;
        const majorLengthSquared = majorX * majorX + majorY * majorY;
        const minorLengthSquared = minorX * minorX + minorY * minorY;

        if (majorLengthSquared < TANGENT_LINE_DEGENERATE_LENGTH ** 2
            || minorLengthSquared < TANGENT_LINE_DEGENERATE_LENGTH ** 2) {
            return null;
        }

        const majorCoordinatePerUnit = (directionX * majorX + directionY * majorY) / majorLengthSquared;
        const minorCoordinatePerUnit = (directionX * minorX + directionY * minorY) / minorLengthSquared;
        const inverseScaleSquared = majorCoordinatePerUnit * majorCoordinatePerUnit
            + minorCoordinatePerUnit * minorCoordinatePerUnit;

        if (inverseScaleSquared < TANGENT_LINE_DEGENERATE_LENGTH ** 2) {
            return null;
        }

        const scale = 1 / Math.sqrt(inverseScaleSquared);

        return [centerX + directionX * scale, centerY + directionY * scale];
    }

    /**
     * 编译直线与椭圆相切关系。
     *
     * @param context 编译上下文。
     * @param lineEntityId 直线实体 ID。
     * @param ellipseEntityId 椭圆实体 ID。
     * @returns 编译结果。
     */
    private compileLineEllipseTangent(
        context: SketchConstraintFormulaCompileContext,
        lineEntityId: string,
        ellipseEntityId: string,
    ): SketchConstraintFormulaCompileResult {
        const [lineStart, lineEnd] = context.linePointHandles.get(lineEntityId)!;
        const ellipse = context.ellipseGeometryStates.get(ellipseEntityId)!;
        const directionX = context.system.read(lineEnd.xId) - context.system.read(lineStart.xId);
        const directionY = context.system.read(lineEnd.yId) - context.system.read(lineStart.yId);
        const directionLength = Math.hypot(directionX, directionY);

        if (directionLength < TANGENT_LINE_DEGENERATE_LENGTH) {
            context.reportUnsupported('相切关系的直线退化为零长度');
            return 'rejected';
        }

        const signedDistance = (directionX / directionLength)
            * (context.system.read(ellipse.center.yId) - context.system.read(lineStart.yId))
            - (directionY / directionLength)
            * (context.system.read(ellipse.center.xId) - context.system.read(lineStart.xId));
        const persistedBranchSign = readSketchConstraintBranchHintNumber(
            context.constraintState,
            'line-curve-tangent',
            'branchSign',
        );
        const branchSign = persistedBranchSign === 1 || persistedBranchSign === -1
            ? persistedBranchSign
            : signedDistance >= 0 ? 1 : -1;

        context.system.addEquation(SketchParametricEquationFactory.lineEllipseTangent(
            `constraint:${context.constraintState.constraintId}`,
            ellipse.center,
            ellipse.majorPositive,
            ellipse.minorPositive,
            lineStart,
            lineEnd,
            branchSign,
        ));
        context.registerResolvedBranchHint(() => ({kind: 'line-curve-tangent', branchSign}));
        return 'compiled';
    }

    /**
     * 编译直线与圆/圆弧相切关系。
     *
     * @param context 编译上下文。
     * @param lineEntityId 直线实体 ID。
     * @param curveEntityId 圆系实体 ID。
     * @returns 编译结果。
     */
    private compileLineCircleTangent(
        context: SketchConstraintFormulaCompileContext,
        lineEntityId: string,
        curveEntityId: string,
    ): SketchConstraintFormulaCompileResult {
        const [lineStart, lineEnd] = context.linePointHandles.get(lineEntityId)!;
        const center = context.centerPointHandles.get(curveEntityId)!;
        const radiusParameterId = context.radiusParameterIds.get(curveEntityId)!;
        const directionX = context.system.read(lineEnd.xId) - context.system.read(lineStart.xId);
        const directionY = context.system.read(lineEnd.yId) - context.system.read(lineStart.yId);
        const directionLength = Math.hypot(directionX, directionY);

        if (directionLength < TANGENT_LINE_DEGENERATE_LENGTH) {
            context.reportUnsupported('相切关系的直线退化为零长度');
            return 'rejected';
        }

        const signedDistance = (directionX / directionLength)
            * (context.system.read(center.yId) - context.system.read(lineStart.yId))
            - (directionY / directionLength)
            * (context.system.read(center.xId) - context.system.read(lineStart.xId));
        const persistedBranchSign = readSketchConstraintBranchHintNumber(
            context.constraintState,
            'line-curve-tangent',
            'branchSign',
        );
        const branchSign = persistedBranchSign === 1 || persistedBranchSign === -1
            ? persistedBranchSign
            : signedDistance >= 0 ? 1 : -1;

        context.system.addEquation(SketchParametricEquationFactory.lineCurveTangent(
            `constraint:${context.constraintState.constraintId}`,
            center,
            lineStart,
            lineEnd,
            radiusParameterId,
            branchSign,
        ));
        context.registerResolvedBranchHint(() => ({kind: 'line-curve-tangent', branchSign}));
        return 'compiled';
    }

    /**
     * 编译两个圆系实体的内切或外切关系。
     *
     * @param context 编译上下文。
     * @param firstEntityId 第一圆系实体 ID。
     * @param secondEntityId 第二圆系实体 ID。
     * @returns 编译结果。
     */
    private compileCirclePairTangent(
        context: SketchConstraintFormulaCompileContext,
        firstEntityId: string,
        secondEntityId: string,
    ): SketchConstraintFormulaCompileResult {
        const firstCenter = context.centerPointHandles.get(firstEntityId)!;
        const secondCenter = context.centerPointHandles.get(secondEntityId)!;
        const firstRadiusId = context.radiusParameterIds.get(firstEntityId)!;
        const secondRadiusId = context.radiusParameterIds.get(secondEntityId)!;
        const centerDistance = Math.hypot(
            context.system.read(secondCenter.xId) - context.system.read(firstCenter.xId),
            context.system.read(secondCenter.yId) - context.system.read(firstCenter.yId),
        );
        const firstRadius = context.system.read(firstRadiusId);
        const secondRadius = context.system.read(secondRadiusId);

        if (centerDistance < TANGENT_LINE_DEGENERATE_LENGTH
            && Math.abs(firstRadius - secondRadius) < TANGENT_LINE_DEGENERATE_LENGTH) {
            context.reportUnsupported('相切关系的两圆弧同心且等径，退化为重合圆');
            return 'rejected';
        }

        const isExternal = centerDistance > Math.max(firstRadius, secondRadius);
        const inferredFirstSign = isExternal || firstRadius >= secondRadius ? 1 : -1;
        const inferredSecondSign = isExternal || secondRadius >= firstRadius ? 1 : -1;
        const persistedFirstSign = readSketchConstraintBranchHintNumber(
            context.constraintState,
            'circle-pair-tangent',
            'signA',
        );
        const persistedSecondSign = readSketchConstraintBranchHintNumber(
            context.constraintState,
            'circle-pair-tangent',
            'signB',
        );
        const firstSign = persistedFirstSign === 1 || persistedFirstSign === -1
            ? persistedFirstSign
            : inferredFirstSign;
        const secondSign = persistedSecondSign === 1 || persistedSecondSign === -1
            ? persistedSecondSign
            : inferredSecondSign;

        context.system.addEquation(SketchParametricEquationFactory.arcArcTangent(
            `constraint:${context.constraintState.constraintId}`,
            firstCenter,
            secondCenter,
            firstRadiusId,
            secondRadiusId,
            firstSign,
            secondSign,
        ));
        context.registerResolvedBranchHint(() => ({
            kind: 'circle-pair-tangent',
            signA: firstSign,
            signB: secondSign,
        }));
        return 'compiled';
    }
}
